/**
 * Centralized utility for engine type map pin icons
 * These are the consistent icons used across:
 * - Marketplace map pins
 * - Wizard location picker
 * - Detail modals
 * - Listing cards
 * - Wizard type selectors
 */

import engineForSale from '@/assets/engine-for-sale.png';
import engineForRent from '@/assets/engine-for-rent.png';
import engineLookingToBuy from '@/assets/engine-looking-to-buy.png';
import engineLookingToRent from '@/assets/engine-looking-to-rent.png';

export type EngineWizardType = 
  | 'engine_sell' 
  | 'engine_rent' 
  | 'engine_buy' 
  | 'engine_rent_request';

// Map engine types to their category-specific icons
export const ENGINE_TYPE_ICONS: Record<EngineWizardType, string> = {
  engine_sell: engineForSale,
  engine_rent: engineForRent,
  engine_buy: engineLookingToBuy,
  engine_rent_request: engineLookingToRent,
};

/**
 * Get the icon for a specific engine listing type
 * @param listingType - The engine listing type
 * @returns The PNG icon path for the engine type
 */
export const getEngineTypeIcon = (listingType?: string): string => {
  if (!listingType) return engineForSale; // default
  return ENGINE_TYPE_ICONS[listingType as EngineWizardType] || engineForSale;
};

/**
 * Get the marker icon for a specific engine listing type (for maps)
 * @param listingType - The engine listing type
 * @returns The PNG icon path for the engine type marker
 */
export const getEngineTypeMarkerIcon = (listingType?: string): string => {
  return getEngineTypeIcon(listingType);
};

/**
 * Configuration for engine type display including labels and colors
 */
export const ENGINE_TYPE_CONFIG: Record<EngineWizardType, {
  label: string;
  shortLabel: string;
  isOffering: boolean;
  color: string;
  bgColor: string;
}> = {
  engine_sell: {
    label: 'Sell Engine',
    shortLabel: 'Sell',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  engine_rent: {
    label: 'Rent Out Engine',
    shortLabel: 'Rent',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  engine_buy: {
    label: 'Looking to Buy',
    shortLabel: 'Buy',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  engine_rent_request: {
    label: 'Looking to Rent',
    shortLabel: 'Rent',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
};

/**
 * Check if an engine listing type is an offering (selling/renting out)
 */
export const isEngineOffering = (listingType?: string): boolean => {
  return listingType === 'engine_sell' || listingType === 'engine_rent';
};

/**
 * Check if an engine listing type is a request (buying/seeking to rent)
 */
export const isEngineRequest = (listingType?: string): boolean => {
  return listingType === 'engine_buy' || listingType === 'engine_rent_request';
};
